"use client";

import { useEffect, useState } from "react";
import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  MinusIcon,
  PlusIcon,
  RotateCcwIcon,
  XIcon,
} from "lucide-react";
import { TransformComponent, TransformWrapper, useControls } from "react-zoom-pan-pinch";
import { Button } from "./button";

/**
 * The one image viewer.
 *
 * Clicking a user-content image anywhere in Forge should land here rather than
 * opening a raw URL in a new tab or growing a second bespoke viewer. It began
 * as the chat-only ExpandedImageDialog; task attachments needed the same thing,
 * and two near-identical viewers is the duplication this replaces.
 *
 * Built on Base UI's Dialog primitives rather than a hand-rolled overlay, and
 * that choice is load-bearing. Callers open this from inside a modal Sheet, and
 * a plain portaled div does not coordinate with the sheet: Escape reached the
 * sheet's handler and closed the task drawer, presses on the controls counted
 * as an outside-press and closed it, and focus stayed trapped in the obscured
 * drawer so keyboard users could never reach the zoom or close controls. Base
 * UI tracks nested dialogs, so it owns Escape, outside-press, focus trap and
 * focus restore, and the parent sheet stands down while this is open.
 *
 * Popup is used raw here, not the styled DialogPopup wrapper, which is a
 * centered max-w-lg card — the wrong shape for a full-bleed image.
 *
 * One layout rule holds the rest together: the popup shrink-wraps the image, so
 * ALL chrome is anchored to the viewport with `fixed`, never to the popup.
 * Anything positioned against the popup inherits the image's size — which broke
 * three separate ways for a narrow image: paging controls overlapped each
 * other, the caption truncated to nothing, and a caption left in flow instead
 * widened the popup until the dimmed area beside the image stopped dismissing.
 * Chrome stays inside the popup in the DOM, so pressing it is never an outside
 * press.
 */

export interface LightboxImage {
  readonly src: string;
  readonly name: string;
  /** Rendered in the caption when known. Attachments have it; chat images do not. */
  readonly sizeBytes?: number | null;
}

export interface ImageLightboxPreview {
  readonly images: readonly LightboxImage[];
  readonly index: number;
}

interface ImageLightboxProps {
  readonly preview: ImageLightboxPreview;
  readonly onClose: () => void;
}

const MIN_SCALE = 1;
const MAX_SCALE = 8;

/** Matches the attachment list's formatting so one image reads the same in both places. */
function formatSize(sizeBytes: number | null | undefined): string | null {
  if (sizeBytes === null || sizeBytes === undefined) return null;
  if (sizeBytes < 1024) return `${sizeBytes}B`;
  if (sizeBytes < 1024 * 1024) return `${Math.round(sizeBytes / 1024)}KB`;
  return `${(sizeBytes / (1024 * 1024)).toFixed(1)}MB`;
}

/**
 * Zoom controls have to live inside TransformWrapper to reach its context, so
 * they are their own component rather than part of the shell.
 */
function ZoomControls() {
  const { zoomIn, zoomOut, resetTransform } = useControls();
  return (
    <div className="fixed bottom-4 left-1/2 z-20 flex -translate-x-1/2 items-center gap-7 rounded-lg bg-black/60 px-2 py-1 backdrop-blur-sm">
      <Button
        type="button"
        size="icon-xs"
        variant="ghost"
        className="text-white/90 hover:bg-white/10 hover:text-white"
        aria-label="Zoom out"
        onClick={() => zoomOut()}
      >
        <MinusIcon />
      </Button>
      <Button
        type="button"
        size="icon-xs"
        variant="ghost"
        className="text-white/90 hover:bg-white/10 hover:text-white"
        aria-label="Reset zoom"
        onClick={() => resetTransform()}
      >
        <RotateCcwIcon />
      </Button>
      <Button
        type="button"
        size="icon-xs"
        variant="ghost"
        className="text-white/90 hover:bg-white/10 hover:text-white"
        aria-label="Zoom in"
        onClick={() => zoomIn()}
      >
        <PlusIcon />
      </Button>
    </div>
  );
}

export function ImageLightbox({ preview, onClose }: ImageLightboxProps) {
  /*
    Only the page offset is state — not a copy of the preview.

    Mirroring the whole preview into state and resyncing it whenever the prop
    identity changed meant any parent re-render snapped the viewer back to the
    image that was first clicked: callers build the preview inline, so every
    render produced a "new" one. Tracking an offset from the caller's index has
    no identity to react to, so paging survives renders it has nothing to do
    with.
  */
  const [offset, setOffset] = useState(0);
  const total = preview.images.length;
  const index = total === 0 ? 0 : (((preview.index + offset) % total) + total) % total;

  const navigateImage = (direction: -1 | 1) => {
    setOffset((existing) => existing + direction);
  };

  /*
    Arrow keys only. Escape belongs to Base UI, which knows this dialog is
    nested inside the sheet and dismisses the innermost one — the thing a
    hand-rolled window listener could not get right.
  */
  useEffect(() => {
    if (total <= 1) return;
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      event.preventDefault();
      // The setter is stable, so the effect has no changing dependency and does
      // not need the callback memoised — React Compiler is active here and the
      // architecture contract rules out useCallback without a measured reason.
      setOffset((existing) => existing + (event.key === "ArrowLeft" ? -1 : 1));
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [total]);

  const item = preview.images[index];
  if (!item) return null;

  const size = formatSize(item.sizeBytes);
  const counter = total > 1 ? `${index + 1} of ${total}` : null;
  const captionDetail = [size, counter].filter((part) => part !== null).join(" · ");

  return (
    <DialogPrimitive.Root
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Backdrop className="fixed inset-0 z-50 bg-black/75" />
        {/*
          Viewport is the full-screen layer; Popup is sized to its content.

          Making Popup itself `fixed inset-0` looked equivalent and was not: it
          covered the backdrop, so a click on the dimmed area landed INSIDE the
          popup, Base UI never saw an outside press, and clicking away stopped
          closing the image — a behaviour chat already had. Keeping the popup
          content-sized leaves the surrounding area genuinely outside it.
        */}
        <DialogPrimitive.Viewport className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6 [-webkit-app-region:no-drag]">
          <DialogPrimitive.Popup
            className="relative flex max-h-[92vh] max-w-[92vw] flex-col outline-none"
            aria-label="Expanded image preview"
          >
            {total > 1 ? (
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="fixed left-2 top-1/2 z-20 -translate-y-1/2 bg-black/60 text-white/90 backdrop-blur-sm hover:bg-black/75 hover:text-white sm:left-4"
                aria-label="Previous image"
                onClick={() => navigateImage(-1)}
              >
                <ChevronLeftIcon className="size-5" />
              </Button>
            ) : null}
            <DialogPrimitive.Close
              render={
                <Button
                  type="button"
                  size="icon-xs"
                  variant="ghost"
                  className="fixed right-2 top-2 z-30 bg-black/60 text-white/90 backdrop-blur-sm hover:bg-black/75 hover:text-white sm:right-4 sm:top-4"
                  aria-label="Close image preview"
                >
                  <XIcon />
                </Button>
              }
            />
            {/*
              Keyed on src so switching images starts the next one at fit rather
              than inheriting the previous image's zoom and pan offset.
            */}
            <TransformWrapper
              key={item.src}
              minScale={MIN_SCALE}
              maxScale={MAX_SCALE}
              doubleClick={{ mode: "toggle", step: 2 }}
              wheel={{ step: 0.2 }}
              panning={{ velocityDisabled: true }}
            >
              <TransformComponent
                wrapperClass="!max-h-[86vh] !max-w-[92vw] cursor-grab active:cursor-grabbing rounded-lg border border-border/70 bg-background shadow-2xl"
                contentClass="!max-h-[86vh] !max-w-[92vw]"
              >
                <img
                  src={item.src}
                  alt={item.name}
                  className="max-h-[86vh] max-w-[92vw] select-none object-contain"
                  draggable={false}
                />
              </TransformComponent>
              <ZoomControls />
            </TransformWrapper>
            {/*
              Absolutely positioned so the caption cannot widen the popup.

              In flow, a long filename stretched this transparent popup out to
              92vw even when the image was narrow — a portrait screenshot, say —
              so the dimmed space beside the image was inside the popup and
              clicking it no longer dismissed. Out of flow, the popup stays the
              width of the image and that space is genuinely backdrop again.
            */}
            <p className="pointer-events-none fixed inset-x-0 bottom-16 mx-auto max-w-[92vw] truncate px-4 text-center text-xs text-white/80">
              {item.name}
              {captionDetail === "" ? "" : ` — ${captionDetail}`}
            </p>
            {total > 1 ? (
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="fixed right-2 top-1/2 z-20 -translate-y-1/2 bg-black/60 text-white/90 backdrop-blur-sm hover:bg-black/75 hover:text-white sm:right-4"
                aria-label="Next image"
                onClick={() => navigateImage(1)}
              >
                <ChevronRightIcon className="size-5" />
              </Button>
            ) : null}
          </DialogPrimitive.Popup>
        </DialogPrimitive.Viewport>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
